import { t } from '@transifex/native';

import type {
	GameReportPayload,
	GameEventsUpdatePayload,
	TimePlayedRequest,
	TimePlayedResponse,
	JerseyNumberChange,
} from 'pkg/actions/games/interface';

import * as actions from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import * as sdk from 'pkg/core/sdk';
import { withSdk } from 'pkg/core/with_sdk';
import { link } from 'pkg/router/utils';

export type {
	GameTeamPayload,
	GameCreatePayload,
	GameUpdatePayload,
	Scorer,
	EventPeriodScore,
	ExtraPeriod,
	EventBasePeriodScore,
	TeamResponse,
	GameReportPayload,
	GameOverviewField,
	GameOverview,
	GameEventResponse,
	PeriodBreakdown,
	GameEventsUpdatePayload,
	GameResultPeriodScore,
	GameResultUpdatePayload,
	TimePlayedRequest,
	UserPlayTime,
	UserTime,
	Position,
	TimePlayedResponse,
} from 'pkg/actions/games/interface';

export {
	TimelineType,
	PeriodScoreDisplayType,
} from 'pkg/actions/games/interface';

export async function updateJerseyNumbers(
	organizationId: number,
	gameId: number,
	jerseyNumbers: JerseyNumberChange[],
	teamId?: number
): Promise<boolean> {
	const req = await sdk.put(
		link(
			endpoints.Games.UpdateJerseyNumbers(organizationId, gameId),
			teamId ? { team_id: teamId } : {}
		),
		{},
		{ jerseyNumbers }
	);

	if (req.ok) {
		return true;
	}

	const resp = await req.json().catch(() => ({}));

	if (resp?.code === 'jersey_number_taken') {
		actions.flashes.show({
			title: t('That number is already taken by {name} in this game', {
				name: resp.name,
			}),
		});
	} else {
		actions.flashes.show({ title: t('Something went wrong') }, req.status);
	}

	return false;
}

export async function saveTimePlayed(
	organizationId: number,
	gameId: number,
	payload: TimePlayedRequest,
	teamId?: number
) {
	return withSdk(
		'post',
		link(
			endpoints.Games.UpsertTimePlayed(organizationId, gameId),
			teamId ? { team_id: teamId } : {}
		)
	)(payload);
}

export async function updateGameReport(
	organizationId: number,
	gameId: number,
	gameReportId: number,
	payload: GameReportPayload
) {
	return withSdk(
		'patch',
		endpoints.Games.UpdateGameReport(organizationId, gameId, gameReportId)
	)(payload);
}

export async function generateTimePlayed(
	organizationId: number,
	gameId: number,
	teamId?: number
): Promise<[ok: boolean, response: TimePlayedResponse]> {
	const request = await sdk.get(
		endpoints.Games.GenerateTimePlayed(organizationId, gameId),
		teamId ? { team_id: teamId } : {}
	);

	if (request.ok) {
		actions.flashes.show({
			title: t('The playtimes have been calculated.'),
		});

		const response = await request.json();

		return [true, response];
	}

	actions.flashes.show({
		title: t('Could not generate the playtime'),
	});

	return [false, null];
}

export async function saveEvents(
	organizationID: number,
	gameID: number,
	payload: GameEventsUpdatePayload
) {
	let request: Response;

	try {
		request = await sdk.put(
			endpoints.Games.UpdateEvents(organizationID, gameID),
			{},
			payload
		);
	} catch {
		actions.flashes.show({
			title: t('Network error. Please try again.'),
		});

		return null;
	}

	if (!request.ok) {
		const response = await request.json().catch(() => ({}));

		actions.flashes.show(
			{
				title: t('Could not update game events'),
				message: response?.error,
			},
			request.status
		);

		return null;
	}

	return request.json();
}

export async function addVideo(
	organizationId: number,
	gameId: number,
	videoId: number
) {
	const onSuccess = () => {
		actions.flashes.show(
			{
				title: t('Video created'),
				message: t('Video was successfully created!'),
			},
			200
		);
	};

	const onFailure = () => {
		actions.flashes.show({
			title: t('Something went wrong when adding the video.'),
		});
	};

	return withSdk(
		'post',
		endpoints.Games.AddVideo(organizationId, gameId, videoId),
		{
			onSuccess,
			onFailure,
		}
	)();
}

export async function deleteVideo(
	organizationId: number,
	gameId: number,
	videoId: number,
	onDeleteSuccess: () => void
) {
	const onSuccess = () => {
		actions.flashes.show({
			title: t('Successfully removed connection.'),
		});

		onDeleteSuccess();
	};

	const onFailure = () => {
		actions.flashes.show({
			title: t('Something went wrong when removing the connection.'),
		});
	};

	return withSdk(
		'delete',
		endpoints.Games.DeleteVideo(organizationId, gameId, videoId),
		{
			onSuccess,
			onFailure,
		}
	)();
}

export async function deleteReport(
	organizationId: number,
	gameId: number,
	gameReportId: number,
	onDeleteSuccess: () => void
) {
	const onSuccess = () => {
		actions.flashes.show({
			title: t('Successfully removed report.'),
		});

		onDeleteSuccess();
	};

	const onFailure = () => {
		actions.flashes.show({
			title: t('Something went wrong when removing the report.'),
		});
	};

	return withSdk(
		'delete',
		endpoints.Games.DeleteGameReport(organizationId, gameId, gameReportId),
		{
			onSuccess,
			onFailure,
		}
	)();
}
